import React, { useState, useEffect } from 'react';

import { Listbox } from '@headlessui/react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { currentDate, dayjsList, dayjsToDateWithText } from '../../utils';

import type { DateWithText } from '../../types/DateWithText';

interface Props {
  className?: string;
  includeBlank?: boolean;
  selectedDateStr: string;
  onDateSelect: (date: string) => void;
}

export const DateSelect: React.FC<Props> = ({
  className = '',
  includeBlank = false,
  selectedDateStr,
  onDateSelect,
}) => {
  const [dates, setDates] = useState<DateWithText[]>([]);
  const [selectedDate, setSelectedDate] = useState<DateWithText | null>(null);

  const onChange = (date: DateWithText) => {
    setSelectedDate(date);
    onDateSelect(date.value);
  };

  useEffect(() => {
    const dateStr = currentDate();
    const dateList = dayjsList(dateStr);
    const dateOptionList = dateList.map((date) => dayjsToDateWithText(date));
    if (includeBlank) {
      dateOptionList.unshift({
        text: '指定なし',
        value: '',
      });
    }
    const initialDate = dateOptionList.find((date) => date.value === selectedDateStr) ?? null;

    setDates(dateOptionList);
    setSelectedDate(initialDate);
  }, [selectedDateStr, includeBlank]);

  return (
    <div className={`${className}`}>
      <Listbox value={selectedDate} onChange={onChange}>
        <div className="relative z-10">
          <Listbox.Button className={styles.button}>
            <span className="block">開示日</span>
            <span className="flex items-center">
              <ArrowDropDownIcon sx={{ width: 18, height: 18 }} />
            </span>
          </Listbox.Button>
          <Listbox.Options className={styles.options}>
            {dates.map((date, idx) => (
              <Listbox.Option
                key={idx}
                className={({ active }) =>
                  `relative cursor-pointer ${active ? 'bg-neutral-100' : ''}`
                }
                value={date}
              >
                {({ selected }) => (
                  <span className="flex items-center py-1.5">
                    <span className={`flex items-center pl-3 text-amber-500`}>
                      <span className="h-5 w-5">{selected ? '✓' : ''}</span>
                    </span>
                    <span
                      className={`${date.sun ? 'text-red' : ''} ${
                        date.sat ? 'text-blue-default' : ''
                      }`}
                    >
                      {date.text}
                    </span>
                  </span>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </div>
      </Listbox>
    </div>
  );
};

const styles = {
  button: `
    bg-white
    border
    border-gray-dark
    hover:bg-neutral-200
    flex
    items-center
    pl-3
    py-1.5
    rounded
    text-sm
    w-20
  `,
  options: `
    absolute
    bg-white
    mt-1
    overflow-auto
    py-1
    ring-1
    ring-gray-dark
    rounded
    shadow-lg
    text-sm
    w-40
  `,
};
